import { GetStaticProps } from 'next';
import { OpOostHomePageFieldsFragment, OpOostInformationFieldsFragment } from 'api/graphql-types';
import Layout from 'modules/layout/layout';
import { ModuleBuilder } from 'modules/module-builder/module-builder';
import client, { httpLink, setPreviewSecret } from 'api/apollo-client';
import { getServerPageGetOpOostHome } from 'api/graphql-helper';
import { initialState as settingsInitialState } from 'redux/stores/settings';
import { Head } from 'modules/head/head';
import { getStrapiParametersFromContext } from 'utils/strapi-parameters';
import HomepageHeader from 'modules/homepage-header/homepage-header';
import { OP_OOST_ROUTES } from 'constants/routes';
import { IBasePageProps } from 'types/page';

interface IOpOostPageProps extends IBasePageProps {
	opOostHomepage?: OpOostHomePageFieldsFragment;
	opOostInformation?: OpOostInformationFieldsFragment;
}

const OpOostHomePage = (props: IOpOostPageProps): React.JSX.Element => {
	const modules = props.opOostHomepage?.modules;
	const seoModule = props?.opOostHomepage?.seo;

	return (
		<Layout navbar={props?.opOostInformation?.navbar} footer={props?.opOostInformation?.footer}>
			<Head seoModule={seoModule} seoParent={props?.opOostInformation?.seo} />
			<HomepageHeader module={props?.opOostHomepage?.header} />
			<ModuleBuilder modules={modules} slug={OP_OOST_ROUTES.HOME.fullPath} />
		</Layout>
	);
};

export const getStaticProps: GetStaticProps<IBasePageProps> = async (ctx) => {
	try {
		const { locale, publicationState, draftMode } = getStrapiParametersFromContext(ctx);

		if (publicationState === 'DRAFT') {
			client.setLink(setPreviewSecret(process.env.STRAPI_PREVIEW_SECRET as string).concat(httpLink));
		}

		const opOostHomePageResult = await getServerPageGetOpOostHome(
			{
				variables: {
					locale,
					publicationState
				}
			},
			client
		);
		const { opOostHomepage, opOostInformation } = opOostHomePageResult.props.data;

		if (opOostHomepage == null) {
			return {
				notFound: true
			};
		}

		return {
			props: {
				opOostHomepage: opOostHomepage?.data?.attributes,
				opOostInformation: opOostInformation?.data?.attributes,
				initialReduxState: {
					settings: {
						...settingsInitialState,
						publicationState,
						draftMode,
						contentType: 'op-oost-homepage',
						contentTypeId: opOostHomepage?.data?.documentId,
						showNeutralStyle: true,
						hideLanguageSwitcher: true,
						hideSearchbar: false,
						isSubsite: true
					}
				}
			},
			revalidate: Number(process.env.NEXT_PUBLIC_REVALIDATE_TIME_IN_SECONDS)
		};
	} catch (error: any) {
		// eslint-disable-next-line no-console
		console.error('error in OpOostHomePage', error);
		return {
			notFound: true
		};
	}
};

export default OpOostHomePage;
